<template>
  <div
    :class="['tag', `tag--${color}`, `tag--${size}`, { 'tag--disabled': disabled, 'tag--stroke': withStroke }]"
    :role="removable ? 'button' : 'presentation'"
    :tabindex="removable ? 0 : -1"
  >
    <sword-icon-wuk v-if="!!iconLeft" :name="iconLeft" />
    <span class="tag__text">
      {{ text }}
    </span>
    <sword-icon-wuk
      v-if="!!iconRight || removable"
      class="tag__action"
      :name="iconRight || 'close'"
      @click.native="remove"
      @keydown.backspace="remove"
    />
  </div>
</template>

<script>
import SwordIconWuk from '@ui-kit/components/icons/SwordIcon.vue';

export default {
  name: 'SwordTag',
  components: { SwordIconWuk },
  props: {
    text: {
      type: String,
      required: true,
    },
    iconLeft: {
      type: String,
      default: '',
    },
    iconRight: {
      type: String,
      default: '',
    },
    color: {
      type: String,
      default: 'black',
      validator(value) {
        return ['black', 'white', 'neutral', 'blue', 'green', 'grey', 'red', 'yellow', 'blue-red'].includes(
          value
        );
      },
    },
    size: {
      type: String,
      default: 'medium',
      validator(value) {
        return ['small', 'medium', 'large'].includes(value);
      },
    },
    withStroke: {
      type: Boolean,
    },
    highlight: {
      type: Boolean,
    },
    removable: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
  },
  methods: {
    remove(e) {
      e.preventDefault();
      e.stopPropagation();

      if (!this.removable) return;
      if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== 'backspace') return;
      this.$emit('remove', e);
    },
  },
};
</script>

<style lang="scss" scoped>
.tag {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  width: max-content;
  height: fit-content;
  border-radius: 0.5rem;
  user-select: none;

  &:focus-visible {
    outline: 2px solid $color-grey-300;
    outline-offset: 1px;
  }

  /* Size variants */
  &--small {
    padding: 0.125rem 0.5rem;
    .tag__text {
      @extend %t4;
      @extend %-t_medium;
    }
  }
  &--medium {
    padding: 0.25rem 1rem;
    .tag__text {
      @extend %t3;
      @extend %-t_medium;
    }
  }
  &--large {
    padding: 0.5rem 1rem;
    .tag__text {
      @extend %t2;
      @extend %-t_medium;
    }
  }

  /* Color variants */
  &--black {
    color: $color-white;
    background-color: $context-color-1;
    &:hover {
      background-color: $context-color-2;
    }
  }
  &--white {
    color: $context-color-1;
    background-color: $color-white;
    &:hover {
      background-color: $color-neutral-300;
    }
  }
  &--neutral {
    color: $context-color-1;
    background-color: $color-neutral-200;
    &:hover {
      background-color: $color-neutral-300;
    }
  }
  &--blue {
    color: $tag-blue-text-color;
    background-color: $color-primary-50;
    &:hover {
      color: $tag-blue-text-hover-color;
      background-color: $color-primary-200;
    }
  }
  &--green {
    color: $color-positive-700;
    background-color: $color-positive-100;
    &:hover {
      color: $color-positive-800;
      background-color: $color-positive-300;
    }
  }
  &--grey {
    color: $color-grey-600;
    background-color: $color-grey-100;
    &:hover {
      color: $color-grey-700;
      background-color: $color-grey-300;
    }
  }
  &--red {
    color: $color-negative-600;
    background-color: $color-negative-50;
    &:hover {
      color: $color-negative-700;
      background-color: $color-negative-100;
    }
  }
  &--yellow {
    color: $context-color-1;
    background-color: $color-secondary-300;
    &:hover {
      background-color: $color-secondary-400;
    }
  }
  &--blue-red {
    background: $gradient-blue-red;
  }

  &--disabled {
    color: $color-grey-500;
    background-color: $color-grey-300;
    cursor: not-allowed;
    pointer-events: none;

    &:hover {
      background-color: $color-grey-300;
    }
  }

  &__action {
    cursor: pointer;
  }

  &--stroke {
    border: 4px solid white;
    border-radius: 0.75rem;
  }

  .sword-icon.svg-icon {
    width: 1rem;
    height: 1rem;
  }
}
</style>
